import { Button } from "@material-ui/core";
import { ProdutoTipo } from "Infrastructure/interfaces/PedidoMeep";
import { CashlessRepository } from "Infrastructure/repositories/api/CashlessRepository";
import { CommandEletronicExtract } from "Infrastructure/repositories/api/ExtractRepository";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { getOrderRepository } from "../../../Infrastructure/repositories/localStorage/getOrderRepository";
import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  OrientationType,
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useUi } from "application/contexts/ui/UIContext";
import { ErrorCodeType } from "application/models/IError";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import ActionModal, {
  IActionModalHandler,
} from "components/actionModal/ActionModal";
import BottomModal from "components/bottomModal/BottomModal";
import { moneyMask, moneyToFloat } from "corss-cutting/masks/money";
import { ICardCashless } from "domains/cashless/aggregates/ICardCashless";
import { Order } from "domains/order/agreggates/order/Order";
import { IEmitedNfce } from "modules/order/domain/models/INfce";
import { IOrderLog } from "modules/order/domain/models/IOrderLog";
import { IPedidoPosDTO } from "modules/order/domain/models/IPedidoPosDTO";
import { Payment } from "modules/order/domain/models/Payment";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { GetError } from "utils/GetError";
import { v4 } from "uuid";
import totemGif from "../../../assets/images/totem.gif";
import { PrepaidExtract } from "../prePaidExtract/PrepaidExtract";
import styles from "./Prepaid.module.scss";
import InputText from "components/inputText/InputText";
import { Keyboard } from "components/keyboard/Keyboard";
import { useSession } from "application/contexts/session/SessionContext";
import { useTranslation } from "i18n/useTranslation";
import { Trans } from "react-i18next";
import { isNullOrEmpty } from "utils/utils";

const cashlessRepository = CashlessRepository();
const orderRepositoryDB = getOrderRepository();

export interface IPrepaid {
  orderProvider: React.RefObject<IOrderProviderRef>;
}

const PrePaid: FC<IPrepaid> = ({ orderProvider }) => {
  const { handleActiveDefaultIdleTimer, resetCounter } = useIdle();
  const { kiosk } = useKiosk();
  const { sessaoIdPos } = useSession();
  const { showLoading, hideLoading, toastFullscreen, toast } = useUi();
  const { addLog } = useAppInsights();
  const { replace, push } = useHistory();
  const { resolution, orientation } = useScreenStore();
  const { getConfigurationProduct } = useCatalogStore();
  const {
    cardData,
    enableSensor,
    disableSensor,
    isPaying,
    readedCard,
    setReadedCard,
    clearCardData,
    getNominalCardDataWithName,
  } = useCardReader();
  const { t } = useTranslation();

  const [extract, setExtract] = useState<CommandEletronicExtract | null>(null);
  const [rechargeValue, setRechargeValue] = useState("0.00");
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [nickname, setNickname] = useState<string>("");

  const revalidateTagRef = useRef<IActionModalHandler>(null);

  const firstLoad = useRef(true);

  const addOrderLog = useCallback(
    async (
      order: IOrderLog | null,
      message: string,
      pedidoPOS?: IPedidoPosDTO,
      payment?: Payment,
      nfce?: IEmitedNfce[]
    ) => {
      try {
        if (order) {
          let orderLog = { ...order, payment, pedidoPOS, nfce };
          addLog("prePaid", { message, orderLog });
          await orderRepositoryDB.add(orderLog, message);
        }
      } catch (error) {
        GetError(
          error,
          "addOrderLog",
          "PrePaid",
          "Falha ao adicionar log",
          ErrorCodeType.GENERAL
        );
      }
    },
    [addLog]
  );

  useEffect(() => {
    enableSensor(true);

    return () => {
      disableSensor();
    };
  }, [enableSensor, disableSensor]);

  useEffect(() => {
    handleActiveDefaultIdleTimer();
  }, [handleActiveDefaultIdleTimer]);

  useEffect(() => {
    if (cardData) {
      resetCounter();
    }
  }, [cardData, resetCounter]);

  const getExtract = useCallback(
    async (cardData: ICardCashless) => {
      if (kiosk) {
        try {
          showLoading();
          const responseExtract = await cashlessRepository.getExtract(
            cardData.associationId,
            kiosk.localId,
            cardData.id,
            false
          );
          if (responseExtract) {
            setExtract(responseExtract);
            resetCounter();
          } else {
            setExtract(null);
          }
        } catch (error) {
          console.log(error);
        } finally {
          hideLoading();
        }
      }
    },
    [hideLoading, kiosk, resetCounter, showLoading]
  );

  useEffect(() => {
    if (cardData && firstLoad.current) {
      firstLoad.current = false;
      if (
        kiosk?.paymentNominalCashless &&
        (isNullOrEmpty(cardData.nickname) || isNullOrEmpty(cardData.password))
      ) {
        toast(t("cashless.messages.invalid_nominal_card"), "error");
        clearCardData();
        enableSensor(true);
        return;
      }
      if (
        cardData?.group?.extensionData !==
        "F6EC7A68-D413-4284-AE3A-43C7DEF285F1"
      ) {
        getExtract(cardData);
      } else {
        const message = (
          <div className="offline-recharce-success-message">
            <p>{t("cashless.messages.not_allowed_line1")}</p>
            <p>{t("cashless.messages.not_allowed_line2")}</p>
          </div>
        );
        toastFullscreen(message, "warning", {
          icon: "error_outline",
          iconSize: 100,
          time: 3000,
        });
      }
    }
  }, [
    cardData,
    getExtract,
    toastFullscreen,
    t,
    kiosk,
    toast,
    clearCardData,
    enableSensor,
  ]);

  const createRechargeOrder = useCallback(
    (value: number, cardData: ICardCashless) => {
      const newOrderId = v4();
      const newOrder: Order = {
        id: newOrderId,
        cartDate: new Date().toISOString(),
        sessionId: sessaoIdPos,
        orderItems: [
          {
            id: v4(),
            name:
              getConfigurationProduct(ProdutoTipo.Recarga)?.name ?? "Recarga",
            category: "",
            price: value,
            realPrice: value,
            totalPrice: value,
            description: null,
            productPrice: value,
            productId: getConfigurationProduct(ProdutoTipo.Recarga)?.id ?? "",
            productType: ProdutoTipo.Recarga,
            quantity: 1,
            imageUrl: "",
            thumbnailUrl: "",
            barcode: null,
            hideCatalog: false,
            orderComposition: null,
            compositions: null,
            printerName: null,
            translations: [],
          },
        ],
        totalValue: value,
        friendlyId:
          kiosk?.kioskName.replace(/\D/gm, "") +
          " " +
          newOrderId.substring(0, 4),
        createdAt: new Date().toISOString(),
        customer: {
          associationId: cardData?.associationId,
          orderId: newOrderId,
          tag: cardData?.tag,
          number: cardData?.tag,
          orderPad: cardData?.tag,
          document: cardData?.holderDocument,
        },
      };
      return newOrder;
    },
    [getConfigurationProduct, kiosk, sessaoIdPos]
  );

  const onSucessPayment = useCallback(async () => {
    if (kiosk) {
      showLoading();
      clearCardData();
      setShowRechargeModal(false);
      setRechargeValue("00.0");

      toastFullscreen(t("cashless.messages.payment_success"), "success", {
        time: 5000,
        onClose: () => {
          replace("/");
        },
      });
      hideLoading();

      isPaying.current = false;
    }
  }, [
    kiosk,
    showLoading,
    clearCardData,
    toastFullscreen,
    hideLoading,
    isPaying,
    replace,
    t,
  ]);

  const handleRecharge = useCallback(async () => {
    if (!cardData || !cardData.associationId) {
      addLog("prePaid", {
        message: "[ERROR]handleRecharge: Falha ao obter dados do cartão",
        cardData,
      });
      return GetError(
        new Error(t("cashless.messages.card_data_error")),
        "handleRecharge",
        "PrePaid",
        undefined,
        ErrorCodeType.PAYMENT
      );
    }

    if (!orderProvider?.current) {
      addLog("prePaid", {
        message: "[ERROR]handleRecharge: Falha ao obter provedor de pagamento",
        cardData,
      });
      return GetError(
        new Error(t("cashless.messages.provider_error")),
        "handleRecharge",
        "PrePaid",
        undefined,
        ErrorCodeType.PAYMENT
      );
    }

    if (moneyToFloat(rechargeValue) >= 1 && !isPaying.current) {
      isPaying.current = true;
      const newOrder = createRechargeOrder(
        moneyToFloat(rechargeValue),
        cardData
      );
      await addOrderLog(
        newOrder,
        `Adicionar crédito tag: ${cardData.tag}`,
        undefined
      );
      try {
        await orderProvider.current.startPayment(newOrder, "Recharge");
        onSucessPayment();
      } catch (error) {
        addLog("prePaid", {
          message: "[ERROR]handleRecharge: Falha ao realizar pagamento",
          error,
        });
        GetError(
          error,
          "handleRecharge",
          "PrePaid",
          undefined,
          ErrorCodeType.PAYMENT
        );
      } finally {
        isPaying.current = false;
      }
    }
  }, [
    addLog,
    addOrderLog,
    cardData,
    createRechargeOrder,
    isPaying,
    onSucessPayment,
    orderProvider,
    rechargeValue,
    t,
  ]);

  const payRecharge = useCallback(async () => {
    if (moneyToFloat(rechargeValue) < 1) {
      toast(t("cashless.messages.min_recharge"), "error");
    } else if (kiosk && kiosk?.doubleValidationOnlineRecharge) {
      setReadedCard(null);
      isPaying.current = false;
      try {
        await revalidateTagRef.current?.open();
      } catch (error) {
        addOrderLog(
          null,
          `[ERROR]payRecharge: Falha na validação dupla de recarga online`,
          undefined
        );
      }
    } else {
      !isPaying.current && handleRecharge();
    }
  }, [
    rechargeValue,
    kiosk,
    toast,
    t,
    setReadedCard,
    isPaying,
    addOrderLog,
    handleRecharge,
  ]);

  const onpressback = useCallback(() => {
    setRechargeValue((prev) => prev.substring(0, prev.length - 1));
  }, []);

  const onpressKeyboard = useCallback((key: string) => {
    setRechargeValue((prev) => prev + key);
  }, []);

  const onClickClose = useCallback(() => {
    setShowRechargeModal(false);
    setRechargeValue("00.0");
  }, []);

  const {
    handleDoubleConfirmation,
    modalMessage,
    autoClose,
    confirmTitle,
    modalTitle,
  } = useMemo(() => {
    let modalMessage = "";
    let confirmTitle = "";
    let modalTitle = t("cashless.double_validation.title_attention");
    let autoClose = 0;
    let handleDoubleConfirmation;
    if (!readedCard) {
      modalMessage = t("cashless.double_validation.message_tap_to_start");
      confirmTitle = t("cashless.double_validation.confirm_cancel");
    } else {
      if (cardData?.tag !== readedCard) {
        autoClose = 3;
        modalMessage = t("cashless.double_validation.message_wrong_card");
        setTimeout(() => {
          push("/");
        }, 3000);
      } else {
        modalTitle = t("cashless.double_validation.title_confirmed");
        modalMessage = t("cashless.double_validation.message_redirect_payment");
        autoClose = 3;
      }
    }

    return {
      modalMessage,
      handleDoubleConfirmation,
      autoClose,
      confirmTitle,
      modalTitle,
    };
  }, [cardData, push, readedCard, t]);

  const getCardDataWithName = useCallback(() => {
    if (nickname.length >= 3) {
      getNominalCardDataWithName(nickname);
    } else {
      toast(t("cashless.nickname.invalid"), "error");
    }
  }, [getNominalCardDataWithName, nickname, toast, t]);

  return (
    <div id={styles.Prepaid}>
      {extract ? (
        <PrepaidExtract
          onClickAdd={() => {
            setShowRechargeModal(true);
          }}
          extract={extract}
        />
      ) : (
        <div className={styles.readCard}>
          <Toolbar hideWelcome title={kiosk?.localName ?? ""} />
          <div className={styles.content}>
            <span
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 45 : 30,
              }}
            >
              {t("cashless.read_card_instruction")}
            </span>
            <img
              src={totemGif}
              alt="Animação"
              style={{
                width:
                  orientation === OrientationType.LANDSCAPE
                    ? 500
                    : resolution === ResolutionType.FULL_HD
                    ? 1000
                    : 700,
                height:
                  orientation === OrientationType.LANDSCAPE
                    ? 500
                    : resolution === ResolutionType.FULL_HD
                    ? 1000
                    : 700,
              }}
            />
            {kiosk?.paymentNominalCashless && kiosk?.searchMeepCardByName && (
              <div className={styles.nicknameContainer}>
                <InputText
                  label={t("cashless.nickname.label")}
                  placeHolder={t("cashless.nickname.placeholder")}
                  value={nickname}
                  type={"text"}
                  onChange={(value: string) => setNickname(value)}
                  onBlur={() => setNickname("")}
                  onPressEnter={getCardDataWithName}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <BottomModal showModal={showRechargeModal} onClickClose={onClickClose}>
        <div className={styles.rechargeValueContainer}>
          <span>
            <Trans i18nKey="cashless.recharge.prompt">
              Informe o <b>valor da recarga</b>
            </Trans>
          </span>
          <div>{moneyMask(rechargeValue)}</div>
          <Keyboard
            type="clear"
            onPressBack={onpressback}
            onKeyPress={onpressKeyboard}
            hideClear
          />
          <Button
            variant="contained"
            fullWidth
            onClick={payRecharge}
            style={{ backgroundColor: "#1a5" }}
          >
            {t("cashless.buttons.add_credit")}
          </Button>
        </div>
      </BottomModal>
      <ActionModal
        ref={revalidateTagRef}
        confirmTitle={confirmTitle}
        message={modalMessage}
        title={modalTitle}
        onClickConfirm={handleDoubleConfirmation}
        hideCloseButton
        oldStyle
        autoCloseDelay={autoClose}
        confirmationDelay={autoClose}
        onAutoClose={handleRecharge}
      />
    </div>
  );
};

export default PrePaid;
